import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, Typography,
} from '@material-ui/core';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  actions, loadFacetValues, saveFacetValues,
  selectIsOpen, selectFacetId, selectFacetType,
  selectCurrentValues, selectIsLoading, selectIsSaving, selectHasChanges,
} from 'slices/FacetValuesSlice';
import BoolMappingEditor from './components/BoolMappingEditor';
import ColorMappingEditor from './components/ColorMappingEditor';

const FacetValueEditorPanel = () => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const { currentShop } = useSelector((state) => state.app);

  const isOpen       = useSelector(selectIsOpen);
  const facetId      = useSelector(selectFacetId);
  const facetType    = useSelector(selectFacetType);
  const values       = useSelector(selectCurrentValues);
  const isLoading    = useSelector(selectIsLoading);
  const isSaving     = useSelector(selectIsSaving);
  const hasChanges   = useSelector(selectHasChanges);

  useEffect(() => {
    if (isOpen && facetId) {
      dispatch(loadFacetValues({ shopId: currentShop, facetId }));
    }
  }, [dispatch, isOpen, facetId, currentShop]);

  const handleClose = () => {
    dispatch(actions.closeEditor());
  };

  const handleSave = () => {
    dispatch(saveFacetValues({ shopId: currentShop, facetId }));
  };

  const handleDiscard = () => {
    dispatch(actions.discardChanges());
  };

  const handleUpdateMapping = (rawValue, mappedValue) => {
    dispatch(actions.updateMapping({ rawValue, mappedValue }));
  };

  const renderEditor = () => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress />
        </div>
      );
    }

    if (!values.length) {
      return (
        <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
          {localizeMessage('customization.filters.valueEditor.noValues')}
        </Typography>
      );
    }

    if (facetType === 'bool') {
      return <BoolMappingEditor values={values} onUpdateMapping={handleUpdateMapping} />;
    }
    if (facetType === 'color') {
      return <ColorMappingEditor values={values} onUpdateMapping={handleUpdateMapping} />;
    }

    return null;
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {localizeMessage('customization.filters.valueEditor.title')}
        {facetId && (
          <Typography variant="caption" color="textSecondary" component="span" style={{ marginLeft: 8 }}>
            ({facetId})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {renderEditor()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDiscard} disabled={!hasChanges || isSaving}>
          {localizeMessage('customization.filters.actions.discard')}
        </Button>
        <Button onClick={handleClose}>
          {localizeMessage('common.close')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? <CircularProgress size={20} /> : localizeMessage('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacetValueEditorPanel;
